import {
  NUTRITION_QUEUE_PERIOD,
  NUTRITION_QUEUE_WINDOWS,
  type NutritionQueue,
} from "@nova-ai/shared"
import { cn } from "../../lib/utils"
import { NutritionHelp } from "./nutrition-help"
import { Pause, Play, RefreshCw } from "lucide-react"
import { useNutritionQueue, type NutritionQueueSearch } from "../../services/use-nutrition-queue"
import { useNutritionRecoverySession } from "../../services/use-nutrition-recovery-verification"
import { formatDateTime } from "../../lib/format-date"
import { Button } from "../ui/button"
import { QueuesShell } from "./queues-shell"
import { NutritionPipeline, NutritionMetrics } from "./nutrition-metrics"
import { NutritionImages } from "./nutrition-images"
import { NutritionEvents } from "./nutrition-events"
import { NutritionDetail } from "./nutrition-detail"

export function NutritionQueuePage({
  search,
  onChange,
}: {
  search: NutritionQueueSearch
  onChange: (search: NutritionQueueSearch) => void
}) {
  const queue = useNutritionQueue(search)
  const recoverySession = useNutritionRecoverySession()
  const { list } = queue
  const data = list.data?.pages[0]
  const rows = [
    ...new Map(
      list.data?.pages.flatMap((page) => page.receipts).map((row) => [row.receipt.id, row]) ?? []
    ).values(),
  ]
  const filterChange = (next: NutritionQueueSearch) => {
    onChange(next)
    if (next.step !== search.step) {
      document
        .getElementById("nutrition-events")
        ?.scrollIntoView({ block: "nearest", behavior: "instant" })
    }
  }
  return (
    <QueuesShell
      actions={<NutritionQueueActions queue={queue} search={search} onChange={onChange} />}
    >
      {list.isPending && (
        <div role="status" aria-label="Loading nutrition queue" className="space-y-4">
          <div className="h-48 rounded-xl bg-muted" />

          <div className="h-40 rounded-xl bg-muted" />

          <p className="text-sm text-muted-foreground">Reading nutrition receipt evidence…</p>
        </div>
      )}

      {list.isPlaceholderData && (
        <p role="status" className="text-xs text-muted-foreground">
          Updating filters… Previous results remain visible until the read completes.
        </p>
      )}

      {list.error && (
        <p
          role="alert"
          className="rounded-xl border border-destructive p-4 text-sm text-destructive"
        >
          {data ? "Displayed data is stale. " : "Nutrition queue could not be read. "}

          {list.error.message}

          {data && ` Last successful read ${formatDateTime(data.observedAt)}.`}
        </p>
      )}

      {data && (
        <>
          <QueueControls controls={data.controls} />

          <NutritionPipeline data={data} search={search} onChange={filterChange} />

          <NutritionMetrics data={data} search={search} onChange={filterChange} />

          <NutritionImages
            images={data.recentImages}
            observedAt={data.observedAt}
            period={search.period ?? "7d"}
            onOpen={(event, store, sku) =>
              onChange({ ...search, event, store, sku, scope: undefined, scopes: undefined })
            }
          />

          <NutritionEvents data={data} rows={rows} search={search} onChange={filterChange} />

          {list.hasNextPage && (
            <div className="text-center">
              <Button
                className="min-h-11"
                disabled={list.isFetchingNextPage}
                onClick={() => {
                  void list.fetchNextPage()
                }}
              >
                {list.isFetchingNextPage ? "Loading receipts…" : "Show more receipts"}
              </Button>
            </div>
          )}
        </>
      )}

      {search.event !== undefined && (
        <NutritionDetail
          key={search.event}
          receiptId={search.event}
          selectedStore={search.store}
          selectedSku={search.sku}
          selectedScope={search.scope}
          failedOnly={search.scopes === "failed"}
          onSelectScope={(scope) =>
            onChange({
              ...search,
              scope: scope.id,
              store: scope.store,
              sku: scope.sku ?? undefined,
            })
          }
          onScopeFilter={(failed) =>
            onChange({
              ...search,
              scopes: failed ? "failed" : undefined,
              scope: undefined,
              store: undefined,
              sku: undefined,
            })
          }
          row={rows.find((row) => row.receipt.id === search.event)}
          queue={queue}
          recoverySession={recoverySession}
          onClose={() =>
            onChange({
              ...search,
              event: undefined,
              store: undefined,
              sku: undefined,
              scope: undefined,
              scopes: undefined,
            })
          }
        />
      )}
    </QueuesShell>
  )
}

function NutritionQueueActions({
  queue,
  search,
  onChange,
}: {
  queue: ReturnType<typeof useNutritionQueue>
  search: NutritionQueueSearch
  onChange: (search: NutritionQueueSearch) => void
}) {
  const { list, paused, setPaused } = queue
  const observedAt = list.data?.pages[0]?.observedAt
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        role="group"
        aria-label="Queue period"
        className="inline-flex rounded-full border border-border bg-muted p-0.5"
      >
        {Object.values(NUTRITION_QUEUE_PERIOD).map((period) => (
          <button
            type="button"
            key={period}
            aria-pressed={(search.period ?? "7d") === period}
            className={cn(
              "min-h-11 rounded-full px-3 text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-7",
              (search.period ?? "7d") === period && "bg-card shadow-card ring-1 ring-border"
            )}
            onClick={() => onChange({ ...search, period })}
          >
            {NUTRITION_QUEUE_WINDOWS[period].label}
          </button>
        ))}
      </div>

      <span
        className="inline-flex items-center gap-2 text-xs tabular-nums text-muted-foreground"
        title={observedAt ? `Last successful read ${formatDateTime(observedAt)}` : undefined}
      >
        <span
          aria-hidden="true"
          className={cn(
            "size-2 rounded-full",
            paused || list.error ? "bg-muted-foreground" : "bg-success"
          )}
        />

        {paused ? "Paused" : "Live"}

        {observedAt
          ? ` · updated ${new Date(observedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
          : ""}
      </span>

      <Button size="sm" className="min-h-11 sm:min-h-8" onClick={() => setPaused(!paused)}>
        {paused ? <Play className="size-4" /> : <Pause className="size-4" />}

        {paused ? "Resume" : "Pause"}
      </Button>

      <Button
        size="sm"
        className="min-h-11 min-w-11 px-2 sm:min-h-8 sm:min-w-8"
        disabled={list.isFetching}
        aria-label="Refresh nutrition queue"
        onClick={() => {
          void list.refetch()
          if (search.event !== undefined) {
            void queue.detail.refetch()
            void queue.attempts.refetch()
          }
        }}
      >
        <RefreshCw className="size-4" />
      </Button>

      <NutritionHelp label="About queue refresh">
        Refreshes every minute while visible. Pause stops reads and recovery verification; workers
        already started continue.
      </NutritionHelp>
    </div>
  )
}

function QueueControls({ controls }: { controls: NutritionQueue["controls"] }) {
  const warnings = [
    controls.dispatchPaused && "Publication dispatch is paused",
    controls.generationEnabled === false && "Generation is disabled",
  ].filter(Boolean)
  return warnings.length ? (
    <p role="status" className="text-xs text-warning">
      {warnings.join(" · ")}
    </p>
  ) : null
}
